/**
 * Matters API — talks to the backend kitchen.
 *
 * This layer:
 *  1. Calls the API endpoints
 *  2. Translates the rich API response into the flat Matter shape
 *     that the frontend pages already understand
 */
import { api } from './api';
import type { Matter, MatterParty } from './mattersStore';

// ─── Types from the API response ────────────────────────────

interface ApiMatter {
  id: string;
  caseNumber: string;
  caseName: string;
  caseType: string;
  status: string;
  priority: string;
  description: string | null;
  courtCaseNumber: string | null;
  opponentParty: string | null;
  opponentContact: { id: string; firstName: string | null; lastName: string | null; company: string | null; email: string | null; type: string } | null;
  opponentCounsel: string | null;
  openedDate: string | null;
  limitationDate: string | null;
  feeType: string | null;
  feeEstimate: string | number | null;
  internalNotes: string | null;
  tags: string[];
  currency: string;
  isHistoricalMatter: boolean;
  originalFilingDate: string | null;
  caseStage: string | null;
  visibility: string;
  previousAdvocateName: string | null;
  previousAdvocateFirm: string | null;
  createdAt: string;
  updatedAt: string;
  practiceArea: { id: string; name: string; slug: string } | null;
  court: { id: string; name: string; shortName: string; type: string } | null;
  judge: { id: string; title: string; fullName: string; shortName: string } | null;
  layClient: { id: string; firstName: string | null; lastName: string | null; company: string | null; email: string | null; type: string } | null;
  instructingSolicitor: { id: string; firstName: string | null; lastName: string | null; firmName: string | null; email: string | null; type: string } | null;
  leadBarrister: { id: string; user: { id: string; firstName: string; lastName: string; memberType: string } } | null;
  juniorBarrister: { id: string; user: { id: string; firstName: string; lastName: string; memberType: string } } | null;
  createdBy: { id: string; firstName: string; lastName: string } | null;
  clerk: { id: string; firstName: string; lastName: string } | null;
  // Detail only
  hearings?: Array<{ id: string; hearingDate: string; hearingType: string; status: string; courtroom: string | null; durationMinutes: number }>;
  feeRecords?: Array<{ id: string; feeType: string; description: string; amount: string; totalAmount: string; feeDate: string }>;
  tasks?: Array<{ id: string; title: string; status: string; priority: string; dueDate: string | null }>;
  _count?: { hearings: number; documents: number; feeRecords: number; tasks: number; notes: number };
}

interface ApiSingleResponse {
  data: ApiMatter;
}

// ─── Transform API → frontend Matter shape ──────────────────

function barristerName(b: ApiMatter['leadBarrister']): string {
  if (!b) return '';
  const u = b.user;
  const suffix = u.memberType === 'silk' ? ' KC' : '';
  return `${u.firstName} ${u.lastName}${suffix}`;
}

function nextHearingDate(m: ApiMatter): string {
  if (!m.hearings?.length) return '—';
  const upcoming = m.hearings[0];
  if (!upcoming) return '—';
  // Append T00:00:00 to force local-time parsing — avoids UTC-midnight timezone shift
  const raw = upcoming.hearingDate ?? '';
  const d = new Date(raw.length === 10 ? raw + 'T00:00:00' : raw);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}

function nextHearingIso(m: ApiMatter): string | null {
  if (!m.hearings?.length) return null;
  const upcoming = m.hearings.find((h) => h.status === 'scheduled' || h.status === 'confirmed');
  return upcoming?.hearingDate ?? null;
}

function apiToMatter(m: ApiMatter): Matter {
  return {
    id: m.id,
    number: m.caseNumber,
    name: m.caseName,
    court: m.court?.shortName ?? '',
    courtId: m.court?.id ?? null,
    barrister: barristerName(m.leadBarrister),
    leadBarristerId: m.leadBarrister?.id ?? null,
    leadBarristerUserId: m.leadBarrister?.user.id ?? null,
    juniorBarristerUserId: m.juniorBarrister?.user.id ?? null,
    status: m.status,
    next: nextHearingDate(m),
    nextHearingIso: nextHearingIso(m),
    area: m.practiceArea?.name ?? '',
    practiceAreaId: m.practiceArea?.id ?? null,
    subArea: '',
    courtroom: '',
    judge: m.judge?.shortName ?? '',
    judgeId: m.judge?.id ?? null,
    feeType: m.feeType ?? '',
    feeEstimate: Number(m.feeEstimate ?? 0),
    riskRating: m.priority,
    summary: m.description ?? '',
    internalNotes: m.internalNotes ?? '',
    isHistoricalMatter: m.isHistoricalMatter ?? false,
    originalFilingDate: m.originalFilingDate ?? null,
    caseStage: m.caseStage ?? null,
    previousAdvocateName: m.previousAdvocateName ?? null,
    previousAdvocateFirm: m.previousAdvocateFirm ?? null,
    role: 'lead',
    clerkUserId: m.clerk?.id ?? null,
    createdAt: new Date(m.createdAt).getTime(),
    createdBy: m.createdBy ? `${m.createdBy.firstName} ${m.createdBy.lastName}` : undefined,
    updatedAt: new Date(m.updatedAt).getTime(),
    access: 'normal',
    visibility: (m.visibility as 'personal' | 'team' | 'chambers' | 'restricted') ?? 'team',
    parties: buildParties(m),
    side: undefined,
    disputeRef: undefined,
  };
}

function buildParties(m: ApiMatter): MatterParty[] {
  const parties: MatterParty[] = [];
  if (m.layClient) {
    const name = m.layClient.company ?? `${m.layClient.firstName ?? ''} ${m.layClient.lastName ?? ''}`.trim();
    parties.push({ name, role: 'claimant', contactId: m.layClient.id });
  }
  if (m.opponentContact) {
    const name = m.opponentContact.company ?? `${m.opponentContact.firstName ?? ''} ${m.opponentContact.lastName ?? ''}`.trim();
    parties.push({ name, role: 'defendant', contactId: m.opponentContact.id });
  } else if (m.opponentParty) {
    parties.push({ name: m.opponentParty, role: 'defendant' });
  }
  return parties;
}

// ─── API calls ──────────────────────────────────────────────

export const MATTERS_FETCH_LIMIT = 200;

export async function fetchMatters(opts?: { search?: string }): Promise<{ matters: Matter[]; truncated: boolean }> {
  const res = await api.get('/matters', {
    params: {
      limit: MATTERS_FETCH_LIMIT,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      ...(opts?.search && { search: opts.search }),
    },
  });
  const payload = res.data?.data ?? res.data;
  const matters: ApiMatter[] = payload.matters ?? [];
  return {
    matters: matters.map(apiToMatter),
    truncated: matters.length >= MATTERS_FETCH_LIMIT,
  };
}

// Search-only fetch — used by CasesListPage when the user has typed a query.
// Returns only the matching matters rather than the full store list.
export async function searchMatters(query: string): Promise<Matter[]> {
  const { matters } = await fetchMatters({ search: query });
  return matters;
}

export async function fetchMatter(id: string): Promise<Matter> {
  const res = await api.get<ApiSingleResponse['data']>(`/matters/${id}`);
  const matter = (res.data as any).data ?? res.data;
  return apiToMatter(matter as ApiMatter);
}

export interface MatterFinancialSummary {
  matterId:    string;
  currency:    string;
  recorded:    number; // SUM(feeRecord.amount) — all work logged
  billed:      number; // SUM(feeRecord.amount WHERE feeNoteId != null) — on an invoice
  unbilled:    number; // recorded - billed
  collected:   number; // SUM(feeNote.amountPaid)
  outstanding: number; // SUM(MAX(0, feeNote.total - feeNote.amountPaid)) for open invoices
  wip: {
    totalAmount:  number;
    totalMinutes: number;
    totalEntries: number;
  };
  trust: {
    accountCount: number;
  };
}

export async function fetchMatterFinancialSummary(id: string): Promise<MatterFinancialSummary> {
  const res = await api.get<{ data: MatterFinancialSummary }>(`/matters/${id}/financial-summary`);
  return res.data.data;
}

export interface MatterIntelligence {
  lastEvents: Array<{
    id: string;
    eventType: string;
    occurredAt: string;
  }>;
  taskSummary: {
    pending: number;
    overdue: number;
    completed: number;
    total: number;
  };
  participants: {
    leadBarrister: string | null;
    juniorBarrister: string | null;
    clerk: string | null;
    instructingSolicitor: { name: string | null; firm: string | null } | null;
  };
  feeSummary: {
    wip: number;
    outstanding: number;
    collected: number;
    currency: string;
  };
  dormancy: {
    isDormant: boolean;
    daysSinceActivity: number;
    /** Canonical "last activity" fact (case events + messages). One definition, every screen. */
    lastActivityAt: string | null;
  };
  feeCapture: {
    completedHearingsWithoutFeeNote: number;
  };
}

export async function fetchMatterIntelligence(id: string): Promise<MatterIntelligence> {
  const res = await api.get<{ data: MatterIntelligence }>(`/matters/${id}/intelligence`);
  return res.data.data;
}

export interface HearingReadiness {
  hasUpcomingHearing: boolean;
  checklist: {
    hearingId: string;
    hearingDate: string;
    hearingType: string;
    daysUntil: number;
    listingConfirmed: boolean;
    documentsUploaded: boolean;
    feeAgreed: boolean;
    tasksComplete: boolean;
  } | null;
}

export async function fetchHearingReadiness(id: string): Promise<HearingReadiness> {
  const res = await api.get<{ data: HearingReadiness }>(`/matters/${id}/readiness`);
  return res.data.data;
}

/**
 * Returns the role-assignment fields of a matter — used by the Add Party
 * modal to pre-fill current values without losing any rich detail in the
 * frontend's flattened `Matter` shape.
 */
export interface MatterRoles {
  layClientId: string | null;
  layClientName: string;
  instructingSolicitorId: string | null;
  instructingSolicitorName: string;
  instructingSolicitorEmail: string | null;
  opponentContactId: string | null;
  opponentContactName: string;
  juniorBarristerId: string | null;
  juniorBarristerName: string;
}

function pickContactName(c: { firstName: string | null; lastName: string | null; company: string | null } | null): string {
  if (!c) return '';
  if (c.company) return c.company;
  return [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
}

export async function fetchMatterRoles(id: string): Promise<MatterRoles> {
  const res = await api.get<ApiSingleResponse['data']>(`/matters/${id}`);
  const m = ((res.data as any).data ?? res.data) as ApiMatter;
  const junior = m.juniorBarrister
    ? `${m.juniorBarrister.user.firstName} ${m.juniorBarrister.user.lastName}${m.juniorBarrister.user.memberType === 'silk' ? ' KC' : ''}`
    : '';
  return {
    layClientId: m.layClient?.id ?? null,
    layClientName: pickContactName(m.layClient),
    instructingSolicitorId: m.instructingSolicitor?.id ?? null,
    instructingSolicitorName: m.instructingSolicitor
      ? (m.instructingSolicitor.firmName ??
        `${m.instructingSolicitor.firstName ?? ''} ${m.instructingSolicitor.lastName ?? ''}`.trim())
      : '',
    instructingSolicitorEmail: m.instructingSolicitor?.email ?? null,
    opponentContactId: m.opponentContact?.id ?? null,
    opponentContactName: pickContactName(m.opponentContact),
    juniorBarristerId: m.juniorBarrister?.id ?? null,
    juniorBarristerName: junior,
  };
}

export interface PartyPayload {
  contactId?: string;
  name: string;
  role: string;
}

// ─── Server timeline (GET /matters/:id/timeline) ───────────────
// The server scopes events to the matter, 404s without access, withholds note events from
// non-barristers, money events without fees access and document events above the caller's tier.

export interface MatterTimelineEvent {
  id: string;
  eventType: string;
  occurredAt: string;
  actor: { id: string; name: string; memberType: string } | null;
}

export interface MatterTimeline {
  events: MatterTimelineEvent[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export async function fetchMatterTimeline(
  matterId: string,
  opts: { page?: number; limit?: number } = {},
): Promise<MatterTimeline> {
  const res = await api.get(`/matters/${matterId}/timeline`, {
    params: { page: opts.page ?? 1, limit: opts.limit ?? 50 },
  });
  const data = (res.data as { data: { events: Array<MatterTimelineEvent & { payload?: unknown }>; pagination: MatterTimeline['pagination'] } }).data;
  // The event payload is deliberately NOT carried into the UI: only note, money and document events
  // are filtered server-side, so free text inside other payloads is not surfaced here.
  return {
    events: data.events.map(({ id, eventType, occurredAt, actor }) => ({ id, eventType, occurredAt, actor })),
    pagination: data.pagination,
  };
}

export interface CreateMatterPayload {
  caseName: string;
  caseType: string;
  status?: string;
  priority?: string;
  description?: string;
  courtCaseNumber?: string;
  practiceAreaId?: string;
  courtId?: string;
  judgeId?: string;
  layClientId?: string;
  instructingSolicitorId?: string;
  /** Richer instructing-solicitor details (primary/alternate phone + email). Creates/enriches the solicitor Contact. */
  instructingSolicitor?: {
    contactId?: string;
    name?: string;
    phone?: string;
    altPhone?: string;
    email?: string;
  };
  leadBarristerId?: string;
  juniorBarristerId?: string;
  clerkId?: string | null; // null = un-assign the designated clerk
  opponentParty?: string;
  opponentContactId?: string;
  opponentCounsel?: string;
  openedDate?: string;
  limitationDate?: string;
  feeType?: string;
  feeEstimate?: number;
  internalNotes?: string;
  tags?: string[];
  isHistoricalMatter?: boolean;
  originalFilingDate?: string;
  caseStage?: string;
  previousAdvocateName?: string;
  previousAdvocateFirm?: string;
  /** A documented reason for proceeding despite a CRITICAL conflict — accepted from a silk or admin only (server-enforced). */
  conflictOverrideReason?: string;
  partyA?: PartyPayload;
  partyB?: PartyPayload;
  visibility?: 'personal' | 'team' | 'chambers' | 'restricted';
}

export async function createMatter(payload: CreateMatterPayload): Promise<Matter> {
  const res = await api.post<ApiSingleResponse['data']>('/matters', payload);
  const matter = (res.data as any).data ?? res.data;
  return apiToMatter(matter as ApiMatter);
}

export async function updateMatter(id: string, payload: Partial<CreateMatterPayload>): Promise<Matter> {
  const res = await api.patch<ApiSingleResponse['data']>(`/matters/${id}`, payload);
  const matter = (res.data as any).data ?? res.data;
  return apiToMatter(matter as ApiMatter);
}

// ─── Matter Lifecycle (replaces delete for all standard users) ───────────────

export type ClosureReason = 'settled' | 'completed' | 'withdrawn' | 'discontinued' | 'other';

export const CLOSURE_REASON_LABELS: Record<ClosureReason, string> = {
  settled:      'Settled',
  completed:    'Completed',
  withdrawn:    'Withdrawn',
  discontinued: 'Discontinued',
  other:        'Other',
};

export async function closeMatter(
  id: string,
  reason: ClosureReason,
  note?: string,
  forceClose?: boolean,
  forceCloseReason?: string,
): Promise<Matter> {
  const res = await api.post<{ data: any }>(`/matters/${id}/close`, {
    reason,
    note,
    ...(forceClose ? { forceClose: true, forceCloseReason } : {}),
  });
  return apiToMatter(res.data.data as ApiMatter);
}

export async function archiveMatter(id: string): Promise<Matter> {
  const res = await api.post<{ data: any }>(`/matters/${id}/archive`);
  return apiToMatter(res.data.data as ApiMatter);
}

export async function restoreMatter(id: string): Promise<Matter> {
  const res = await api.post<{ data: any }>(`/matters/${id}/restore`);
  return apiToMatter(res.data.data as ApiMatter);
}

/** Platform super-admin only — requires matterNumber confirmation */
export async function deleteMatter(id: string, matterNumber?: string, reason?: string): Promise<void> {
  await api.delete(`/matters/${id}`, { data: { matterNumber, reason } });
}

export interface LinkedBarrister {
  barristerId: string;
  userId: string;
  firstName: string;
  lastName: string;
  memberType: string;
}

/** For clerks: fetches all barristers linked to this clerk via the BarristerClerk join table */
export async function fetchMyBarristers(): Promise<LinkedBarrister[]> {
  const res = await api.get<{ data: LinkedBarrister[] }>('/barristers/my-barristers');
  return res.data.data;
}

// ─── Matter closure validation ───────────────────────────────

export interface ClosureBlocker {
  type: string;
  count: number;
  label: string;
  items: Array<{ id: string; summary: string }>;
}

export interface ClosureCheckResult {
  canClose: boolean;
  blockers: ClosureBlocker[];
  /** True only when caller is admin AND there are blockers — lets the UI show a force-close path */
  canForceClose: boolean;
}

export async function checkMatterClosure(
  matterId: string,
  targetStatus: string,
): Promise<ClosureCheckResult> {
  const res = await api.get<{ data: ClosureCheckResult }>(
    `/matters/${matterId}/closure-check`,
    { params: { targetStatus } },
  );
  return res.data.data;
}

// ─── Closure summary (the "this case is finished" picture) ───

export interface ClosureSummary {
  matter: {
    id: string;
    caseNumber: string;
    caseName: string;
    status: string;
    currency: string;
    openedAt: string;
    closedAt: string | null;
    closureReason: string | null;
    practiceArea: string | null;
    court: string | null;
    layClient: string | null;
    instructingSolicitor: string | null;
    leadBarrister: string | null;
  };
  /** null when the viewer lacks the `fees` capability on this matter (server-enforced). */
  finance: { currency: string; raised: number; collected: number; outstanding: number; feeNoteCount: number } | null;
  documents: { count: number; recent: Array<{ id: string; name: string; mimeType: string; uploadedAt: string }> };
  timeline: { count: number; recent: Array<{ id: string; eventType: string; occurredAt: string }> };
  hearings: { total: number; completed: number };
}

export async function fetchClosureSummary(matterId: string): Promise<ClosureSummary> {
  const res = await api.get<{ data: ClosureSummary }>(`/matters/${matterId}/closure-summary`);
  return res.data.data;
}

// ─── Matter financial state (where does the money stand / what's next) ───

export type MatterFinancialStateKey =
  | 'no_billing'
  | 'needs_billing'
  | 'awaiting_payment'
  | 'payment_needs_attention'
  | 'payment_overdue'
  | 'payment_at_risk'
  | 'financially_complete';

export type FinancialActionType =
  | 'raise_fee_note'
  | 'send_reminder'
  | 'follow_up'
  | 'call_solicitor'
  | 'close_matter'
  | 'none';

export type PaymentHealth = 'healthy' | 'needs_attention' | 'at_risk';

export interface MatterFinancialState {
  state: MatterFinancialStateKey;
  health: PaymentHealth;
  label: string;
  currency: string;
  outstanding: number;
  unbilled: number;
  partiallyPaid: boolean;
  focus: {
    feeNoteId: string;
    feeNoteNumber: string;
    outstanding: number;
    sentDaysAgo: number | null;
    overdueDays: number;
    remindersSent: number;
    lastChasedDaysAgo: number | null;
  } | null;
  recommendedAction: { type: FinancialActionType; label: string };
  facts: string[];
}

export async function fetchMatterFinancialState(matterId: string): Promise<MatterFinancialState> {
  const res = await api.get<{ data: MatterFinancialState }>(`/matters/${matterId}/financial-state`);
  return res.data.data;
}

// ─── Payment story (the complete money narrative) ───

export type PaymentStoryEventType = 'raised' | 'sent' | 'chase' | 'payment' | 'complete';

export interface PaymentStoryEvent {
  id: string;
  type: PaymentStoryEventType;
  at: string;
  feeNoteNumber?: string;
  amount?: number;
  method?: string;
  by?: string;
  note?: string | null;
  allocatedTo?: string[];
  writtenOff?: boolean;
}

export interface MatterPaymentStory {
  currency: string;
  complete: boolean;
  events: PaymentStoryEvent[];
}

export async function fetchMatterPaymentStory(matterId: string): Promise<MatterPaymentStory> {
  const res = await api.get<{ data: MatterPaymentStory }>(`/matters/${matterId}/payment-story`);
  return res.data.data;
}

// ─── Court Situation ("Court Tomorrow / Court Today") ───

export type CourtConfidence = 'yes' | 'almost' | 'no' | 'not_set';

export interface CourtPrepItem { key: string; label: string; done: boolean }
export interface CourtObjectiveItem { key: string; label: string; present: boolean; detail?: string }

export interface CourtSituation {
  active: boolean;
  urgency?: 'today' | 'tomorrow' | 'this_week';
  daysUntil?: number;
  hearing?: {
    id: string;
    type: string;
    typeLabel: string;
    date: string;
    durationMinutes: number | null;
    court: string | null;
    courtroom: string | null;
    judge: string | null;
    isVirtual: boolean;
    videoLink: string | null;
  };
  matter?: { id: string; caseNumber: string; caseName: string };
  objective?: CourtObjectiveItem[];
  professional?: { items: CourtPrepItem[]; done: number; total: number };
  operational?: { items: CourtPrepItem[]; done: number; total: number };
  confidence?: CourtConfidence;
  recommendedAction?: { type: string; label: string; key?: string } | null;
}

export async function fetchMatterCourtSituation(matterId: string): Promise<CourtSituation> {
  const res = await api.get<{ data: CourtSituation }>(`/matters/${matterId}/court-situation`);
  return res.data.data;
}

export async function updateCourtPrep(
  hearingId: string,
  panel: 'prof' | 'ops',
  key: string,
  done: boolean,
): Promise<CourtSituation> {
  const res = await api.patch<{ data: CourtSituation }>(`/hearings/${hearingId}/court-prep`, { panel, key, done });
  return res.data.data;
}

export async function setCourtConfidence(hearingId: string, value: CourtConfidence): Promise<CourtSituation> {
  const res = await api.post<{ data: CourtSituation }>(`/hearings/${hearingId}/confidence`, { value });
  return res.data.data;
}

// ─── Money Requiring Attention (practice-wide payment intelligence) ───

export interface MoneyAttentionItem {
  matterId: string;
  caseName: string;
  caseNumber: string;
  clientName: string | null;
  outstanding: number;
  state: MatterFinancialStateKey;
  health: PaymentHealth;
  label: string;
  recommendedAction: { type: FinancialActionType; label: string };
  focusFeeNoteId: string;
  sentDaysAgo: number | null;
  overdueDays: number;
  remindersSent: number;
}

export interface MoneyRequiringAttention {
  currency: string;
  totalOutstanding: number;
  totalRequiringAttention: number;
  attentionCount: number;
  items: MoneyAttentionItem[];
}

export async function fetchMoneyRequiringAttention(): Promise<MoneyRequiringAttention> {
  const res = await api.get<{ data: MoneyRequiringAttention }>('/matters/money-attention');
  return res.data.data;
}

/** Downloads the closing-summary dossier PDF (the v1 archive package). */
export async function downloadClosingSummaryPdf(matterId: string, caseNumber: string): Promise<void> {
  const res = await api.get(`/matters/${matterId}/closing-summary.pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const safeNumber = caseNumber.replace(/[^a-zA-Z0-9_-]/g, '_');
  const a = document.createElement('a');
  a.href = url;
  a.download = `closing-summary-${safeNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 100);
}
